import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { AgeStepper } from '@/components/shared/AgeStepper';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useSession } from '@/hooks/useSession';
import { ageToGroup, isAgeGroupSupported } from '@/lib/age-group';
import { trackEvent } from '@/lib/analytics';
import { getManagerContactUrl } from '@/lib/city';
import { getMaxBridge } from '@/lib/max-bridge';
import { CITY_LABELS } from '@/types/city';

/**
 * Экран 2 — ввод имени и возраста ребёнка.
 * Источник: docs/design/01_design_iter1_base.pdf (экран 03 в макете).
 *
 * Логика flow:
 *  1. Имя (можно псевдоним) + возраст → «Продолжить».
 *  2. Если возраст в группе 9-11 → /instruction.
 *  3. Если возраст в 6-8 или 12-14 → внутренний state переключается на
 *     заглушку «Скоро будет доступно» (без отдельного роута, т.к. это часть
 *     flow ChildInfo).
 */
export default function ChildInfo() {
  const navigate = useNavigate();
  const { state, setChild, setDraftName } = useSession();

  // При возврате на экран — предзаполняем имя из draftName (если был сохранён
  // при попытке с unsupported age). Возраст всегда сбрасывается — так
  // пользователь явно перевыбирает (защита от случайного повтора 7→stub).
  const [name, setName] = useState(state.draftName ?? '');
  const [age, setAge] = useState<number | null>(null);
  const [unsupportedAge, setUnsupportedAge] = useState<number | null>(null);

  const canContinue = name.trim().length > 0 && age !== null;

  const handleContinue = () => {
    if (!age) return;
    const trimmed = name.trim();
    const group = ageToGroup(age);
    if (!group) return;

    if (!isAgeGroupSupported(group)) {
      // Inline-заглушка для 6-8 / 12-14. Сохраняем имя в draftName,
      // чтобы при возврате на /child-info поле было предзаполнено.
      setDraftName(trimmed);
      setUnsupportedAge(age);
      return;
    }

    // setChild автоматически очищает draftName (см. SessionContext reducer).
    setChild({ name: trimmed, age }, group);
    navigate('/instruction');
  };

  if (unsupportedAge !== null) {
    return <UnsupportedAgeBlock age={unsupportedAge} onClose={() => setUnsupportedAge(null)} />;
  }

  return (
    <ScreenLayout
      onBack={() => navigate(-1)}
      cta={
        <Button onClick={handleContinue} disabled={!canContinue} fullWidth>
          Продолжить
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold leading-tight text-text-primary">
            Расскажите о&nbsp;ребёнке
          </h2>
          <p className="text-base leading-relaxed text-text-secondary">
            Это нужно, чтобы тест звучал лично и подобрал вопросы по&nbsp;возрасту.
          </p>
        </div>

        <Input
          label="Имя ребёнка"
          value={name}
          onChange={setName}
          placeholder="Например, Михаил"
          autoComplete="off"
          autoCapitalize="words"
          spellCheck={false}
        />

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text-primary">Возраст</span>
          <AgeStepper value={age} onChange={setAge} />
        </div>
      </div>
    </ScreenLayout>
  );
}

/**
 * Заглушка для возрастных групп 6-8 и 12-14.
 * Эти группы будут реализованы в Фазе 3.
 */
function UnsupportedAgeBlock({ age, onClose }: { age: number; onClose: () => void }) {
  const navigate = useNavigate();
  const { state } = useSession();
  // state.city resolved быть должен — AppShell редиректит на /invalid-link при
  // null до /child-info. Defensive narrowing: если как-то проскочили — render
  // ничего, навигация туда не доходит.
  if (state.city === null) return null;
  const cityLabel = CITY_LABELS[state.city];

  const group = ageToGroup(age);

  const handleContact = () => {
    if (state.city === null) return;
    const url = getManagerContactUrl(state.city);
    trackEvent('manager_contact_click', { city: state.city, age_group: group ?? 'unknown' });
    try {
      getMaxBridge().openLink(url);
    } catch {
      // Если bridge недоступен — открываем через window.open (для теста в браузере).
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleClose = () => {
    onClose();
    navigate('/');
  };

  return (
    <ScreenLayout
      onBack={onClose}
      cta={
        <div className="flex flex-col gap-2">
          <Button onClick={handleContact} fullWidth>
            Связаться с менеджером
          </Button>
          <Button onClick={handleClose} fullWidth variant="secondary">
            Закрыть
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <span className="inline-flex w-fit items-center rounded-full bg-warning/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-warning">
            Скоро будет доступно
          </span>
          <h2 className="text-2xl font-bold leading-tight text-text-primary">
            Тест для возраста {age} лет открывается чуть позже
          </h2>
          <p className="text-base leading-relaxed text-text-secondary">
            Сейчас тест работает только для детей 9–11 лет — это самая массовая группа в нашей
            школе. Для других возрастов мы открываем тест в ближайшие недели.
          </p>
          <p className="text-base leading-relaxed text-text-secondary">
            Если хотите записаться на пробное занятие — напишите нам.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-surface p-4 text-sm text-text-secondary">
          Менеджер из филиала <span className="font-semibold text-text-primary">{cityLabel}</span>{' '}
          ответит вам в боте в MAX и подберёт удобное время.
        </div>
      </div>
    </ScreenLayout>
  );
}
